import React, { useState } from "react";
import styles from "./ReportModal.module.css";

interface ReportModalProps {
    questionId: string;
    userId: string;
    onClose: () => void;
    onSubmit: (reason: string, details: string) => Promise<void>;
}

const REPORT_REASONS = [
    { value: "incorrect_answer", label: "Incorrect Answer", description: "The correct answer marked is wrong" },
    { value: "unclear_question", label: "Unclear Question", description: "The question is confusing or ambiguous" },
    { value: "typo", label: "Typo", description: "There's a spelling or grammar error" },
    { value: "duplicate", label: "Duplicate", description: "This question appears elsewhere" },
    { value: "outdated", label: "Outdated Info", description: "The information is no longer accurate" },
    { value: "other", label: "Other", description: "Another issue not listed above" },
];

const ReportModal: React.FC<ReportModalProps> = ({
    questionId,
    userId,
    onClose,
    onSubmit,
}) => {
    const [selectedReason, setSelectedReason] = useState<string>("");
    const [details, setDetails] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async () => {
        if (!selectedReason) {
            setError("Please select a reason");
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            await onSubmit(selectedReason, details.trim());
            (window as any).showNotification?.("Report submitted. Thank you!", "success");
            onClose();
        } catch (err: any) {
            setError(err.message || "Failed to submit report");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <h3>Report an Issue</h3>
                    <button className={styles.closeButton} onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className={styles.body}>
                    <p className={styles.subtitle}>What's wrong with this question?</p>

                    <div className={styles.reasonsList}>
                        {REPORT_REASONS.map((reason) => (
                            <label
                                key={reason.value}
                                className={`${styles.reasonOption} ${selectedReason === reason.value ? styles.selected : ""}`}
                            >
                                <input
                                    type="radio"
                                    name="reason"
                                    value={reason.value}
                                    checked={selectedReason === reason.value}
                                    onChange={() => setSelectedReason(reason.value)}
                                />
                                <div className={styles.reasonContent}>
                                    <span className={styles.reasonLabel}>{reason.label}</span>
                                    <span className={styles.reasonDescription}>{reason.description}</span>
                                </div>
                            </label>
                        ))}
                    </div>

                    <div className={styles.detailsSection}>
                        <label htmlFor="details">Additional details (optional)</label>
                        <textarea
                            id="details"
                            value={details}
                            onChange={(e) => setDetails(e.target.value)}
                            placeholder="Provide more context about the issue..."
                            rows={3}
                            maxLength={500}
                        />
                    </div>

                    {error && <div className={styles.error}>{error}</div>}
                </div>

                <div className={styles.footer}>
                    <button
                        className={styles.cancelButton}
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        className={styles.submitButton}
                        onClick={handleSubmit}
                        disabled={isSubmitting || !selectedReason}
                    >
                        {isSubmitting ? "Submitting..." : "Submit Report"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReportModal;
